// src/utils/riskScoringUtils.ts

export interface RiskCounts {
	good: number;
	medium: number;
	bad: number;
	null: number;
}

export interface NumericRiskCounts {
	total: number;
	sum: number;
	count: number;
}

/**
 * Calculate weighted risk score using a more robust scoring system
 * This system properly weights bad variants as more significant than good ones
 */
export const calculateWeightedRiskScore = (counts: RiskCounts): 'good' | 'medium' | 'bad' | null => {
	const total = counts.good + counts.medium + counts.bad;

	// If no data, return null
	if (total === 0) {
		return null;
	}

	// Calculate percentages
	const badPercent = (counts.bad / total) * 100;
	const mediumPercent = (counts.medium / total) * 100;
	const goodPercent = (counts.good / total) * 100;

	// Weighted scoring system - bad variants are more significant
	// Bad variants have higher impact on overall risk

	// HIGH RISK (RED): If significant proportion of variants are bad
	if (badPercent >= 40) {
		return 'bad'; // 40%+ bad variants = high risk
	}

	// MEDIUM-HIGH RISK (RED): Lower threshold if there are many bad variants
	if (badPercent >= 25 && mediumPercent >= 25) {
		return 'bad'; // 25%+ bad AND 25%+ medium = high risk
	}

	// MEDIUM RISK (YELLOW): Moderate number of bad variants OR many medium variants
	if (badPercent >= 15) {
		return 'medium'; // 15%+ bad variants = at least medium risk
	}

	if (mediumPercent >= 50) {
		return 'medium'; // 50%+ medium variants = medium risk
	}

	// Mixed results - default to medium unless overwhelmingly good
	if (badPercent > 0 && goodPercent < 80) {
		return 'medium'; // Any bad variants + not overwhelmingly good = medium
	}

	// LOW RISK (GREEN): Only if very favorable profile
	if (goodPercent >= 80 && badPercent === 0) {
		return 'good'; // 80%+ good AND no bad variants = low risk
	}

	if (goodPercent >= 90) {
		return 'good'; // 90%+ good variants = low risk (allows for a few bad ones)
	}

	// Default to medium for unclear cases
	return 'medium';
};

/**
 * Alternative conservative scoring system - even more strict
 */
export const calculateConservativeRiskScore = (counts: RiskCounts): 'good' | 'medium' | 'bad' | null => {
	const total = counts.good + counts.medium + counts.bad;

	if (total === 0) {
		return null;
	}

	const badPercent = (counts.bad / total) * 100;
	const mediumPercent = (counts.medium / total) * 100;
	const goodPercent = (counts.good / total) * 100;

	// VERY STRICT: Any significant bad variants push to higher risk

	// HIGH RISK (RED)
	if (badPercent >= 30) {
		return 'bad'; // 30%+ bad = high risk
	}

	if (badPercent >= 20 && mediumPercent >= 30) {
		return 'bad'; // 20%+ bad AND 30%+ medium = high risk
	}

	// MEDIUM RISK (YELLOW)
	if (badPercent > 0) {
		return 'medium'; // ANY bad variants = at least medium risk
	}

	if (mediumPercent >= 40) {
		return 'medium'; // 40%+ medium = medium risk
	}

	// LOW RISK (GREEN) - very strict criteria
	if (goodPercent >= 85 && badPercent === 0 && mediumPercent <= 15) {
		return 'good'; // 85%+ good, no bad, ≤15% medium = low risk
	}

	// Default to medium
	return 'medium';
};

/**
 * Balanced scoring system - middle ground between current and conservative
 * Updated to be slightly more lenient
 */
export const calculateBalancedRiskScore = (counts: RiskCounts): 'good' | 'medium' | 'bad' | null => {
	const total = counts.good + counts.medium + counts.bad;

	if (total === 0) {
		return null;
	}

	const badPercent = (counts.bad / total) * 100;
	const mediumPercent = (counts.medium / total) * 100;
	const goodPercent = (counts.good / total) * 100;

	// HIGH RISK (RED) - slightly higher thresholds
	if (badPercent >= 40) {
		return 'bad'; // 40%+ bad = high risk (was 35%)
	}

	if (badPercent >= 30 && mediumPercent >= 40) {
		return 'bad'; // 30%+ bad AND 40%+ medium = high risk (was 25% and 35%)
	}

	// MEDIUM RISK (YELLOW) - slightly higher threshold
	if (badPercent >= 15) {
		return 'medium'; // 15%+ bad = at least medium risk (was 10%)
	}

	if (mediumPercent >= 65) {
		return 'medium'; // 65%+ medium = medium risk (was 60%)
	}

	if (badPercent > 0 && goodPercent < 70) {
		return 'medium'; // Some bad variants + not mostly good = medium (was 75%)
	}

	// LOW RISK (GREEN) - slightly lower thresholds
	if (goodPercent >= 70 && badPercent === 0) {
		return 'good'; // 70%+ good AND no bad = low risk (was 75%)
	}

	if (goodPercent >= 80) {
		return 'good'; // 80%+ good = low risk (was 85%)
	}

	// Default to medium
	return 'medium';
};

/**
 * Light scoring system - more lenient thresholds to reduce bad scores
 */
export const calculateLightRiskScore = (counts: RiskCounts): 'good' | 'medium' | 'bad' | null => {
	const total = counts.good + counts.medium + counts.bad;

	if (total === 0) {
		return null;
	}

	const badPercent = (counts.bad / total) * 100;
	const mediumPercent = (counts.medium / total) * 100;
	const goodPercent = (counts.good / total) * 100;

	// HIGH RISK (RED) - much higher thresholds to reduce bad scores
	if (badPercent >= 60) {
		return 'bad'; // 60%+ bad = high risk (was 45%)
	}

	if (badPercent >= 45 && mediumPercent >= 45) {
		return 'bad'; // 45%+ bad AND 45%+ medium = high risk (was 30% and 40%)
	}

	// MEDIUM RISK (YELLOW) - higher thresholds
	if (badPercent >= 25) {
		return 'medium'; // 25%+ bad = medium risk (was 15%)
	}

	if (mediumPercent >= 75) {
		return 'medium'; // 75%+ medium = medium risk (was 65%)
	}

	if (badPercent > 10 && goodPercent < 60) {
		return 'medium'; // >10% bad variants + not mostly good = medium (was >0% and <70%)
	}

	// LOW RISK (GREEN) - more lenient thresholds
	if (goodPercent >= 60 && badPercent <= 10) {
		return 'good'; // 60%+ good AND ≤10% bad = low risk (was 70% and 0%)
	}

	if (goodPercent >= 75) {
		return 'good'; // 75%+ good = low risk (was 80%)
	}

	// Default to good instead of medium for lighter scoring
	return 'good';
};

/**
 * Legacy scoring system that matches the old JavaScript implementation exactly
 * This uses numeric risk factors and averages them like the old system
 */
export const calculateLegacyRiskScore = (numericCounts: NumericRiskCounts): 'good' | 'medium' | 'bad' | null => {
	if (numericCounts.count === 0) {
		return null;
	}

	const averageRiskFactor = numericCounts.sum / numericCounts.count;

	// These thresholds match the old JavaScript system exactly
	if (averageRiskFactor === 0) {
		return null; // Grey in old system
	} else if (averageRiskFactor === 1) {
		return 'good'; // Green
	} else if (averageRiskFactor > 1 && averageRiskFactor < 2.1) {
		return 'medium'; // Orange
	} else if (averageRiskFactor >= 2.1) {
		return 'bad'; // Red
	}

	return 'medium'; // Default fallback
};

/**
 * Legacy trait scoring system that matches the old JavaScript implementation exactly
 */
export const calculateLegacyTraitRiskScore = (numericCounts: NumericRiskCounts): 'good' | 'medium' | 'bad' | null => {
	if (numericCounts.count === 0) {
		return null;
	}

	const averageRiskFactor = numericCounts.sum / numericCounts.count;

	// These thresholds match the old JavaScript system exactly for traits
	if (averageRiskFactor === 0) {
		return null; // Grey in old system
	} else if (averageRiskFactor === 1) {
		return 'good'; // Green
	} else if (averageRiskFactor > 1 && averageRiskFactor < 1.6) {
		return 'medium'; // Orange (light green comment in old code but orange color)
	} else if (averageRiskFactor >= 1.6 && averageRiskFactor < 2.2) {
		return 'medium'; // Orange
	} else if (averageRiskFactor >= 2.2 && averageRiskFactor < 2.9) {
		return 'medium'; // Orange (dark orange comment)
	} else if (averageRiskFactor >= 2.9) {
		return 'bad'; // Red
	}

	return 'medium'; // Default fallback
};

/**
 * Get risk score using the specified scoring method
 */
export const calculateRiskScore = (
	counts: RiskCounts,
	method: 'weighted' | 'conservative' | 'light' | 'balanced' | 'legacy' = 'balanced'
): 'good' | 'medium' | 'bad' | null => {
	switch (method) {
		case 'weighted':
			return calculateWeightedRiskScore(counts);
		case 'conservative':
			return calculateConservativeRiskScore(counts);
		case 'light':
			return calculateLightRiskScore(counts);
		case 'legacy':
			// For legacy method, we need to convert to numeric approach
			// This is handled separately in the wheel components
			return calculateBalancedRiskScore(counts);
		case 'balanced':
		default:
			return calculateBalancedRiskScore(counts);
	}
};

/**
 * Calculate numeric risk score using legacy approach
 */
export const calculateNumericRiskScore = (
	numericCounts: NumericRiskCounts,
	isTraitLevel: boolean = false
): 'good' | 'medium' | 'bad' | null => {
	if (isTraitLevel) {
		return calculateLegacyTraitRiskScore(numericCounts);
	} else {
		return calculateLegacyRiskScore(numericCounts);
	}
};

/**
 * Debug function to show scoring breakdown
 */
export const getScoreBreakdown = (counts: RiskCounts, method: 'weighted' | 'conservative' | 'balanced' = 'balanced') => {
	const total = counts.good + counts.medium + counts.bad;

	if (total === 0) {
		return {
			total: 0,
			percentages: { good: 0, medium: 0, bad: 0 },
			score: null,
			reasoning: 'No data available'
		};
	}

	const percentages = {
		good: Math.round((counts.good / total) * 100),
		medium: Math.round((counts.medium / total) * 100),
		bad: Math.round((counts.bad / total) * 100)
	};

	const score = calculateRiskScore(counts, method);

	let reasoning = '';
	switch (score) {
		case 'bad':
			reasoning = `High risk: ${percentages.bad}% bad variants (threshold varies by method)`;
			break;
		case 'medium':
			reasoning = `Medium risk: ${percentages.bad}% bad, ${percentages.medium}% medium variants`;
			break;
		case 'good':
			reasoning = `Low risk: ${percentages.good}% good variants with minimal bad variants`;
			break;
		default:
			reasoning = 'No risk data available';
	}

	return {
		total,
		counts,
		percentages,
		score,
		reasoning,
		method
	};
};